/**
 * 分镜方案 API 服务
 * 对接后端 /api/v1/storyboard-resources/storyboard-plans
 */
import apiClient from './client';

/** 分镜方案响应（与后端 StoryboardPlanResponse 对应） */
export interface StoryboardPlanResponse {
  planId: string;
  storyboardId: string;
  projectId: string;
  planName: string;
  description?: string | null;
  orderIndex: number;
  planType: number;
  metaData?: Record<string, unknown> | null;
  selectedResourceId?: string | null;
  /** 定稿图在方案内的版本序号（供拼接 @分镜[方案名V{seq}]） */
  selectedResourceSeq?: number | null;
  coverUrl?: string | null;
  coverFileSize?: number | null;
  thumbnailUrl?: string | null;
  /** 定稿图的人像资产类型：1=真人形象，2=虚拟人像；仅图片方案返回 */
  humanAssetType?: number | null;
  /** 定稿图的人像资产状态：0=处理中，1=已激活，2=失败；仅图片方案返回 */
  humanAssetStatus?: number | null;
  isFinalized: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectStoryboardPlansResponse {
  storyboardId: string;
  imagePlans: StoryboardPlanResponse[];
  videoPlans: StoryboardPlanResponse[];
}

export interface CreateStoryboardPlanDto {
  planName: string;
  description?: string;
  orderIndex?: number;
  planType: number;
  metaData?: Record<string, unknown>;
}

export interface UpdateStoryboardPlanDto {
  planName?: string;
  description?: string;
  orderIndex?: number;
  metaData?: Record<string, unknown>;
  coverUrl?: string;
  isFinalized?: boolean;
}

export const storyboardPlansApi = {
  /**
   * GET /api/v1/storyboard-resources/projects/{projectId}/storyboard-plans
   * 获取当前项目下所有分镜图片和视频方案，按分镜分组。
   */
  async getProjectStoryboardPlans(projectId: string): Promise<ProjectStoryboardPlansResponse[]> {
    const data = (await apiClient.get(
      `/storyboard-resources/projects/${projectId}/storyboard-plans`
    )) as ProjectStoryboardPlansResponse[];
    return Array.isArray(data) ? data : [];
  },

  /**
   * GET /api/v1/storyboard-resources/storyboards/{storyboardId}/plans
   * 获取分镜下指定类型方案（planType: 1=图片，2=视频）
   */
  async getPlans(storyboardId: string, planType: number): Promise<StoryboardPlanResponse[]> {
    const data = (await apiClient.get(`/storyboard-resources/storyboards/${storyboardId}/plans`, {
      params: { planType },
    })) as StoryboardPlanResponse[];
    return Array.isArray(data) ? data : [];
  },

  /**
   * POST /api/v1/storyboard-resources/storyboards/{storyboardId}/plans
   * 新建方案
   */
  async createPlan(
    storyboardId: string,
    dto: CreateStoryboardPlanDto
  ): Promise<StoryboardPlanResponse> {
    return (await apiClient.post(
      `/storyboard-resources/storyboards/${storyboardId}/plans`,
      dto
    )) as StoryboardPlanResponse;
  },

  /**
   * GET /api/v1/storyboard-resources/storyboard-plans/{planId}
   * 获取单个方案
   */
  async getPlan(planId: string): Promise<StoryboardPlanResponse> {
    return (await apiClient.get(
      `/storyboard-resources/storyboard-plans/${planId}`
    )) as StoryboardPlanResponse;
  },

  /**
   * PATCH /api/v1/storyboard-resources/storyboard-plans/{planId}
   * 更新方案
   */
  async updatePlan(planId: string, dto: UpdateStoryboardPlanDto): Promise<StoryboardPlanResponse> {
    return (await apiClient.patch(
      `/storyboard-resources/storyboard-plans/${planId}`,
      dto
    )) as StoryboardPlanResponse;
  },

  /**
   * DELETE /api/v1/storyboard-resources/storyboard-plans/{planId}
   * 删除方案
   */
  async deletePlan(planId: string): Promise<void> {
    await apiClient.delete(`/storyboard-resources/storyboard-plans/${planId}`);
  },

  /**
   * POST /api/v1/storyboard-resources/storyboard-plans/{planId}/default
   * 设置方案当前定稿资源
   */
  async setSelectedResource(planId: string, resourceId: string): Promise<StoryboardPlanResponse> {
    return (await apiClient.post(`/storyboard-resources/storyboard-plans/${planId}/default`, {
      selectedResourceId: resourceId,
    })) as StoryboardPlanResponse;
  },
};
